import React from "react";
import { lighten } from "polished";
import styled, { DefaultTheme, css } from "styled-components";

type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "danger"
  | "success"
  | "info"
  | "warning"
  | "gradient";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  $variant?: ButtonVariant;
  size?: "sm" | "md" | "lg" | "xl";
  $size?: "sm" | "md" | "lg" | "xl";
  icon?: React.ReactNode;
  children: React.ReactNode;
  circular?: boolean;
  $circular?: boolean;
  fullWidth?: boolean;
  $fullWidth?: boolean;
}

const variants = {
  primary: ({ theme }: { theme: DefaultTheme }) => `
    color: white;
    border: none;
    background: linear-gradient(90deg, ${theme.colors.verae[500]} 0%, ${theme.colors.purple[500]} 100%);
    transition: all 0.3s ease;
    
    &:hover:not(:disabled) {
      background: linear-gradient(90deg, ${theme.colors.verae[600]} 0%, ${theme.colors.purple[600]} 100%);
      transform: translateY(-2px);
      box-shadow: ${theme.shadows.md};
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: ${theme.shadows.sm};
    }
  `,
  secondary: ({ theme }: { theme: DefaultTheme }) => `
    color: white;
    border: none;
    background: ${theme.colors.purple[500]};
    
    &:hover:not(:disabled) {
      background: ${theme.colors.purple[600]};
    }
  `,
  outline: ({ theme }: { theme: DefaultTheme }) => `
    color: ${theme.colors.verae[600]};
    border: 1px solid ${theme.colors.verae[200]};
    background: transparent;
    
    &:hover:not(:disabled) {
      background: ${theme.colors.verae[50]};
      border-color: ${theme.colors.verae[300]};
    }
  `,
  ghost: ({ theme }: { theme: DefaultTheme }) => `
    color: ${theme.colors.verae[600]};
    border: none;
    background: transparent;
    
    &:hover:not(:disabled) {
      background: ${theme.colors.verae[50]};
    }
  `,
  danger: ({ theme }: { theme: DefaultTheme }) => `
    color: white;
    border: none;
    background: ${theme.colors.error};
    
    &:hover:not(:disabled) {
      background: ${lighten(-0.05, theme.colors.error)};
    }
  `,
  success: ({ theme }: { theme: DefaultTheme }) => `
    color: white;
    border: none;
    background: ${theme.colors.success};
    
    &:hover:not(:disabled) {
      background: ${lighten(-0.05, theme.colors.success)};
    }
  `,
  info: ({ theme }: { theme: DefaultTheme }) => `
    color: white;
    border: none;
    background: ${theme.colors.info};
    
    &:hover:not(:disabled) {
      background: ${lighten(-0.05, theme.colors.info)};
    }
  `,
  warning: ({ theme }: { theme: DefaultTheme }) => `
    color: white;
    border: none;
    background: ${theme.colors.warning};
    
    &:hover:not(:disabled) {
      background: ${lighten(-0.05, theme.colors.warning)};
    }
  `,
  gradient: ({ theme }: { theme: DefaultTheme }) => `
    color: white;
    border: none;
    background: linear-gradient(90deg, ${theme.colors.verae[500]} 0%, ${theme.colors.purple[500]} 100%);
    
    &:hover:not(:disabled) {
      background: linear-gradient(90deg, ${theme.colors.verae[600]} 0%, ${theme.colors.purple[600]} 100%);
    }
  `,
};

const sizeStyles = {
  sm: css`
    padding: 6px 12px;
    font-size: 0.875rem;
    height: 32px;
  `,
  md: css`
    padding: 8px 16px;
    font-size: 0.9375rem;
    height: 40px;
  `,
  lg: css`
    padding: 10px 20px;
    font-size: 1rem;
    height: 48px;
  `,
  xl: css`
    padding: 12px 24px;
    font-size: 1.125rem;
    height: 56px;
  `,
};

const StyledButton = styled.button<{
  $variant?: ButtonVariant;
  $size?: "sm" | "md" | "lg" | "xl";
  $hasIcon?: boolean;
  $circular?: boolean;
  $fullWidth?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  outline: none;
  position: relative;
  overflow: hidden;
  font-family: ${({ theme }) => theme.fontFamily.sans};
  letter-spacing: 0.01em;
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  }

  .button-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  ${({ $variant = "primary", theme }) => variants[$variant]({ theme })};
  ${({ $size = "md" }) => sizeStyles[$size]};
  ${({ $hasIcon }) =>
    $hasIcon &&
    css`
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    `}
    
  ${({ $circular, $size = "md" }) =>
    $circular &&
    css`
      border-radius: 50%;
      padding: 0;
      width: ${$size === "sm" ? "32px" : $size === "md" ? "40px" : $size === "lg" ? "48px" : "56px"};
      height: ${$size === "sm" ? "32px" : $size === "md" ? "40px" : $size === "lg" ? "48px" : "56px"};
      min-width: ${$size === "sm" ? "32px" : $size === "md" ? "40px" : $size === "lg" ? "48px" : "56px"};
      min-height: ${$size === "sm" ? "32px" : $size === "md" ? "40px" : $size === "lg" ? "48px" : "56px"};
    `}

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: none;
  }
`;

export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  $variant,
  size = "md",
  $size,
  icon,
  circular,
  $circular,
  fullWidth,
  $fullWidth,
  children,
  ...props
}) => {
  return (
    <StyledButton
      $variant={$variant || variant}
      $size={$size || size}
      $hasIcon={!!icon}
      $circular={$circular || circular}
      $fullWidth={$fullWidth || fullWidth}
      {...props}
    >
      {icon && <span className="button-icon">{icon}</span>}
      {children}
    </StyledButton>
  );
};
